"use client";

import { memo, useCallback } from "react";

import { useRovingFocus } from "../../hooks";
import { useNavbar } from "./navbar.context";
import {
  navbarMenuItemStyles,
  navbarMenuItemIconStyles,
  navbarMenuItemContentStyles,
  navbarMenuItemLabelStyles,
  navbarMenuItemDescriptionStyles,
} from "./styles/navbar-menu-item.styles";

import type { PolymorphicNavbarMenuItemProps } from "./types/navbar-menu-item.types";

export const NavbarMenuItem = memo(<C extends React.ElementType = "a">(props: PolymorphicNavbarMenuItemProps<C>) => {
  const { label, href, description, icon, className, component, onClick, ...rest } = props;

  const { toggleMenu } = useNavbar();

  const handleKeypress = useRovingFocus({ orientation: "vertical" });

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      toggleMenu(null);
      onClick?.(event);
    },
    [toggleMenu, onClick],
  );

  const Component = component || "a";

  return (
    <li role="none">
      <Component
        href={href}
        role="menuitem"
        className={navbarMenuItemStyles({ className })}
        onKeyDown={handleKeypress}
        onClick={handleClick}
        {...rest}
      >
        {icon ? <span className={navbarMenuItemIconStyles()}>{icon}</span> : null}

        <div className={navbarMenuItemContentStyles()}>
          <span className={navbarMenuItemLabelStyles()}>{label}</span>
          {description && <span className={navbarMenuItemDescriptionStyles()}>{description}</span>}
        </div>
      </Component>
    </li>
  );
}) as <C extends React.ElementType = "a">(props: PolymorphicNavbarMenuItemProps<C>) => React.ReactElement;
